// src/services/abstract/poc/cidDownloadLogger.interface.ts

/**
 * Download step identifiers for CID download logging.
 * Workflow:
 * 1. Browser requests file from network (requesting)
 * 2. First block received from peer (fetching)
 * 3. Subsequent blocks received (chunk-progress)
 * 4. File stored in browser's Helia blockstore (stored)
 * 5. File decrypted if encrypted (decrypting)
 * 6. Download complete (complete)
 */
export type DownloadStep =
  | 'requesting'
  | 'fetching'
  | 'chunk-progress'
  | 'stored'
  | 'decrypting'
  | 'complete'
  | 'error';

/**
 * Step labels for human-readable logging.
 * Numbering matches HAP's CidUploadLogger for consistency:
 * - Steps 1-3 are browser-specific (requesting, etc.)
 * - Steps 4-5 match HAP (fetching blocks, file stored)
 * - Steps 6-7 are browser-specific (decrypting, complete)
 */
export const DOWNLOAD_STEP_LABELS: Record<DownloadStep, string> = {
  'requesting': '3. Requesting from network',
  'fetching': '4. Fetching blocks',
  'chunk-progress': '4. Fetching blocks',
  'stored': '5. File stored',
  'decrypting': '6. Decrypting',
  'complete': '7. Complete',
  'error': 'ERROR'
};

/**
 * Abstract base class for CID download logging.
 * Tracks the progress of file downloads through the browser's P2P system.
 */
export abstract class CidDownloadLogger {
  /**
   * Log a download step for a specific CID
   * @param cid - The content identifier being downloaded
   * @param step - The download step being logged
   * @param details - Additional details to include in the log entry
   */
  abstract log(cid: string, step: DownloadStep, details?: Record<string, unknown>): void;
}
